import { AppsAndGamesTray } from '../../../domain/apps-and-games-tray';
import {
  CreateInstalledAppsItemRequestApiModel,
  CreateInstalledAppsPageItemRequestApiModel,
  CreateRecommendedAppsItemRequestApiModel,
  CreateRecommendedAppsPageItemRequestApiModel,
  InstalledAppsPageItemApiModel,
  RecommendedAppsItemApiModel,
  RecommendedAppsPageItemApiModel,
  UpdateInstalledAppsItemRequestApiModel,
  UpdateInstalledAppsPageItemRequestApiModel,
  UpdateRecommendedAppsItemRequestApiModel,
  UpdateRecommendedAppsPageItemRequestApiModel,
} from '@cxb/editor-stb-api';
import { mapperPipe, remainderMapper } from 'osn-shared';
import {
  nameLocalizedMapperDomainToApi,
  scheduledDateMapperDomainToApi,
  targetingMapperDomainToApi,
} from '../../mapping/create-tray-domain-to-api';
import { Application } from '../../../domain/application';
import { AppsAndGamesTraySubtype } from './apps-and-games-tray-subtype.model';
import { InstalledAppsItemApiModel } from '@cxb/editor-stb-api/model/models';
import {
  commonMapper,
  nameLocalizedMapperApiToDomain,
  scheduledDateMapperApiToDomain,
  targetingMapperApiToDomain,
} from '../../mapping/create-tray-api-to-domain';
import { AppsAndGamesTrayFormViewModel } from './apps-and-games-tray-form.view-model';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';
import {
  commonMapperDomainToViewModel,
  nameLocalizedMapperDomainToViewModel,
  scheduledDateMapperDomainToViewModel,
  targetingMapper,
} from '../create-tray-screen/mapping/create-tray-domain-to-vm';
import {
  commonMapperViewModelToDomain,
  nameLocalizedMapperViewModelToDomain,
  scheduledDateMapperViewModelToDomain,
} from '../create-tray-screen/mapping/create-tray-vm-to-domain';

// 'AppsAndGamesInstalledApps'
// 'AppsAndGamesRecommendedApps'

/**
 * DOMAIN -> API
 */

const mapApplicationDomainModelToApiModel = (
  app: Application,
  index: number
): CreateRecommendedAppsItemRequestApiModel &
  UpdateRecommendedAppsItemRequestApiModel &
  CreateInstalledAppsItemRequestApiModel &
  UpdateInstalledAppsItemRequestApiModel => {
  return {
    order: index + 1,
    appId: app.id, // NOT app.appId, that is the Bundle ID and the backend expects the app.id in the appId field
  };
};

export const mapAppsAndGamesInstalledAppsTrayDomainModelToApiModel = (
  item: AppsAndGamesTray,
  pageVersionId?: string,
  tenantId?: string
): CreateInstalledAppsPageItemRequestApiModel & UpdateInstalledAppsPageItemRequestApiModel => {
  const commonProperties = mapperPipe(item, nameLocalizedMapperDomainToApi, targetingMapperDomainToApi, scheduledDateMapperDomainToApi);

  return remainderMapper(commonProperties, () => ({
    pageVersionId,
    items: item.apps.map(mapApplicationDomainModelToApiModel),
    tenantId,
  }));
};

export const mapAppsAndGamesRecommendedAppsTrayDomainModelToApiModel = (
  item: AppsAndGamesTray,
  pageVersionId?: string,
  tenantId?: string
  // eslint-disable-next-line sonarjs/no-identical-functions
): CreateRecommendedAppsPageItemRequestApiModel & UpdateRecommendedAppsPageItemRequestApiModel => {
  const commonProperties = mapperPipe(item, nameLocalizedMapperDomainToApi, targetingMapperDomainToApi, scheduledDateMapperDomainToApi);

  // eslint-disable-next-line sonarjs/no-identical-functions
  return remainderMapper(commonProperties, () => ({
    pageVersionId,
    items: item.apps.map(mapApplicationDomainModelToApiModel),
    tenantId,
  }));
};

/**
 * API -> DOMAIN
 */

export const mapAppsAndGamesInstalledAppsTrayApiModelToDomainModel = (
  installedAppsPageItem: InstalledAppsPageItemApiModel
): AppsAndGamesTray => {
  return remainderMapper(
    mapperPipe(
      installedAppsPageItem,
      commonMapper,
      nameLocalizedMapperApiToDomain,
      targetingMapperApiToDomain,
      scheduledDateMapperApiToDomain
    ),
    () => ({
      type: 'AppsAndGamesInstalledApps',
      subType: AppsAndGamesTraySubtype.InstalledApps,
      apps: installedAppsPageItem.items.map((item: InstalledAppsItemApiModel) => {
        return {
          id: item.app.id,
          appId: item.app.appId,
          name: item.app.appName,
          icon: item.app.icon,
        };
      }),
    })
  );
};

export const mapAppsAndGamesRecommendedAppsTrayApiModelToDomainModel = (
  recommendedAppsPageItem: RecommendedAppsPageItemApiModel
): AppsAndGamesTray => {
  return remainderMapper(
    mapperPipe(
      recommendedAppsPageItem,
      commonMapper,
      nameLocalizedMapperApiToDomain,
      targetingMapperApiToDomain,
      scheduledDateMapperApiToDomain
    ),
    () => ({
      type: 'AppsAndGamesRecommendedApps',
      subType: AppsAndGamesTraySubtype.RecommendedApps,
      // eslint-disable-next-line sonarjs/no-identical-functions
      apps: recommendedAppsPageItem.items.map((item: RecommendedAppsItemApiModel) => {
        return {
          id: item.app.id,
          appId: item.app.appId,
          name: item.app.appName,
          icon: item.app.icon,
        };
      }),
    })
  );
};

/**
 * DOMAIN -> VIEWMODEL
 */

export const mapAppsAndGamesInstalledAppsTrayDomainModelToViewModel = (
  appsAndGamesItem: AppsAndGamesTray
): AppsAndGamesTrayFormViewModel => {
  const commonProperties = mapperPipe(
    appsAndGamesItem,
    commonMapperDomainToViewModel,
    nameLocalizedMapperDomainToViewModel,
    targetingMapper,
    scheduledDateMapperDomainToViewModel
  );

  return remainderMapper(commonProperties, () => ({
    type: TrayFormViewModelType.AppsAndGames,
    subType: AppsAndGamesTraySubtype.InstalledApps,
    apps: appsAndGamesItem.apps,
  }));
};

export const mapAppsAndGamesRecommendedAppsTrayDomainModelToViewModel = (
  appsAndGamesItem: AppsAndGamesTray
): AppsAndGamesTrayFormViewModel => {
  const commonProperties = mapperPipe(
    appsAndGamesItem,
    commonMapperDomainToViewModel,
    nameLocalizedMapperDomainToViewModel,
    targetingMapper,
    scheduledDateMapperDomainToViewModel
  );

  return remainderMapper(commonProperties, () => ({
    type: TrayFormViewModelType.AppsAndGames,
    subType: AppsAndGamesTraySubtype.RecommendedApps,
    apps: appsAndGamesItem.apps,
  }));
};

/**
 * VIEWMODEL -> DOMAIN
 */

export const mapAppsAndGamesTrayViewModelToDomainModel = (vm: AppsAndGamesTrayFormViewModel): AppsAndGamesTray => {
  const commonProperties = mapperPipe(
    vm,
    commonMapperViewModelToDomain,
    nameLocalizedMapperViewModelToDomain,
    targetingMapper,
    scheduledDateMapperViewModelToDomain
  );

  return remainderMapper(commonProperties, () => ({
    type: vm.subType === AppsAndGamesTraySubtype.InstalledApps ? 'AppsAndGamesInstalledApps' : 'AppsAndGamesRecommendedApps',
    subType: vm.subType || undefined,
    apps:
      vm.apps?.map((app) => ({
        id: app['appForm'].id,
        appId: app['appForm'].appId,
        name: app['appForm'].appName,
        icon: app['appForm'].icon,
      })) ?? undefined,
  }));
};
